// apps/vendor/src/features/products/add/importSheet.ts
//
// File handling for the product import (2026-10-13; plan
// docs/plans/2026-10-13-vendor-import-mapping.md). Pure functions so the
// parse rules are testable without a DOM:
//   - read a CSV or XLSX into rows (SheetJS — quoted cells, commas inside a
//     cell, BOM and numeric cells all handled, unlike the old split(','));
//   - check the file before anything is sent (size, rows, extension, the
//     `Import` sheet, header present);
//   - build the downloadable template (Import + Printway SKUs + Guide).
//
// Column names are the wire contract with the API (English on purpose);
// everything the vendor READS is translated by the caller.

import * as XLSX from 'xlsx';

export const IMPORT_SHEET = 'Import';
export const SKU_SHEET = 'Printway SKUs';
export const GUIDE_SHEET = 'Guide';

export const PRODUCT_COLUMNS = [
  'product_code',
  'product_name',
  'product_type',
  'low_stock_threshold',
  'description',
  'size_guide_url',
  'product_templates_url',
] as const;

// 2026-10-14 (plan D14) — `base_cost` is per row (each SKU its own cost),
// `extra_cost` is gone, short/long description merged into `description`.
export const VARIANT_COLUMNS = [
  'printway_sku',
  'vendor_sku',
  'size',
  'color',
  'base_cost',
  'processing_time_days',
  'capacity_per_day',
  'weight_grams',
  'length_cm',
  'width_cm',
  'height_cm',
  'image_urls',
  'enabled',
] as const;

export const TEMPLATE_COLUMNS = [...PRODUCT_COLUMNS, ...VARIANT_COLUMNS] as const;

export const REQUIRED_COLUMNS = [
  'product_code',
  'product_name',
  'base_cost',
  'printway_sku',
  'processing_time_days',
  'capacity_per_day',
  'weight_grams',
  'length_cm',
  'width_cm',
  'height_cm',
] as const;

export const MAX_FILE_BYTES = 2 * 1024 * 1024;
/**
 * CR5-01 — MAX_FILE_BYTES measures the COMPRESSED file; an XLSX of 1 MB can
 * unzip to hundreds of MB of sheet XML and stall `XLSX.read` for a minute.
 * A real 500-row template (plus the Printway SKU sheet) is a few MB unzipped.
 */
export const MAX_UNZIPPED_BYTES = 20 * 1024 * 1024;
/** CR5-01 / CR6-02 — a parse still running after this is abandoned (PARSE_TIMEOUT). */
export const PARSE_TIMEOUT_MS = 5000;
export const MAX_ROWS = 500;
/** Blank rows tolerated past MAX_ROWS before the declared range is refused. */
const ROW_SLACK = 50;
/**
 * R5-01 — widest range read, counted from the sheet's first column: the
 * template's columns plus room for a few unknown ones (the server names those
 * in UNKNOWN_FIELD). A cell further right — a stray `x` in column XFD — is
 * ignored, otherwise `blankrows` + `defval` would allocate rows × 16 384 cells.
 */
export const MAX_COLUMNS = 40;

export type SheetProblem =
  | 'FILE_TOO_LARGE'
  | 'FILE_TYPE'
  | 'FILE_EMPTY'
  | 'NO_IMPORT_SHEET'
  | 'TOO_MANY_ROWS'
  | 'MISSING_COLUMNS'
  | 'DUPLICATE_COLUMNS'
  | 'READ_FAILED'
  /** CR6-02 — the parse ran past PARSE_TIMEOUT_MS: the file is too heavy, not broken. */
  | 'PARSE_TIMEOUT';

export type ImportRow = { row: number } & Record<string, string | number>;

export type ReadResult =
  | { ok: true; rows: ImportRow[] }
  | { ok: false; problem: SheetProblem; columns?: string[] };

function extensionOf(name: string): 'csv' | 'xlsx' | null {
  const lower = name.toLowerCase();
  if (lower.endsWith('.csv')) return 'csv';
  if (lower.endsWith('.xlsx')) return 'xlsx';
  return null;
}

const KNOWN_HEADERS: ReadonlySet<string> = new Set<string>([...TEMPLATE_COLUMNS, 'sku_prefix', 'cost_price_cents']);

/**
 * R2-04 — header cells compare case-insensitively: `PRODUCT_CODE` or
 * ` Product_Code ` is the template column `product_code`. The API keys rows
 * on the canonical (lower-case) name; an unknown header is passed through as
 * typed (trimmed) so the server can name it in UNKNOWN_FIELD.
 */
export function canonicalHeader(raw: string): string {
  const trimmed = raw.trim();
  const lower = trimmed.toLowerCase();
  return KNOWN_HEADERS.has(lower) ? lower : trimmed;
}

/** Bytes of a CSV/XLSX → rows keyed by header, 1-based sheet row numbers. */
export function parseWorkbook(data: ArrayBuffer | string, kind: 'csv' | 'xlsx'): ReadResult {
  let wb: XLSX.WorkBook;
  try {
    if (kind === 'csv') {
      const text = typeof data === 'string' ? data : new TextDecoder('utf-8').decode(data);
      // Strip a UTF-8 BOM (Excel "CSV UTF-8" adds one) so the first header
      // reads `product_code`, not `\uFEFFproduct_code`.
      wb = XLSX.read(text.replace(/^\uFEFF/, ''), { type: 'string', raw: true });
    } else {
      wb = XLSX.read(data, { type: 'array' });
    }
  } catch {
    return { ok: false, problem: 'READ_FAILED' };
  }
  // R2-04 — `import`, `IMPORT `… are the same sheet to a person.
  const sheetName =
    kind === 'csv'
      ? wb.SheetNames[0]
      : wb.SheetNames.find((n) => n.trim().toLowerCase() === IMPORT_SHEET.toLowerCase());
  if (!sheetName) return { ok: false, problem: 'NO_IMPORT_SHEET' };
  const sheet = wb.Sheets[sheetName]!;
  // CR4-01 — `!ref` is what the file CLAIMS; a 16 KB XLSX can declare
  // A1:Z1000000 and `blankrows:true` would then allocate a million rows. Clamp
  // the end of the range to the last real cell (the start stays: R3-03 row
  // numbers count from `s.r`), and refuse an over-long sheet before parsing.
  if (sheet['!ref']) {
    const declared = XLSX.utils.decode_range(sheet['!ref']);
    const colCap = declared.s.c + Math.max(MAX_COLUMNS, TEMPLATE_COLUMNS.length) - 1;
    let maxR = -1;
    let maxC = -1;
    for (const key of Object.keys(sheet)) {
      if (key.charCodeAt(0) === 33 /* '!' */) continue;
      const { r, c } = XLSX.utils.decode_cell(key);
      // R5-01 — a cell past the column cap is never read, so it must not
      // stretch the range either (rows or columns).
      if (c > colCap) continue;
      if (r > maxR) maxR = r;
      if (c > maxC) maxC = c;
    }
    if (maxR < declared.s.r) return { ok: false, problem: 'FILE_EMPTY' };
    if (maxR - declared.s.r > MAX_ROWS + ROW_SLACK) return { ok: false, problem: 'TOO_MANY_ROWS' };
    if (maxR < declared.e.r || maxC < declared.e.c) {
      sheet['!ref'] = XLSX.utils.encode_range({
        s: declared.s,
        e: { r: Math.min(declared.e.r, maxR), c: Math.max(declared.s.c, Math.min(declared.e.c, maxC)) },
      });
    }
  }
  // R3-03 — keep blank rows so a matrix index maps back to the real sheet
  // row; the range may also start below row 1 (header on row 3 of an XLSX).
  const matrix = XLSX.utils.sheet_to_json<Array<string | number | null>>(sheet, { header: 1, defval: null, raw: true, blankrows: true });
  const firstRow = sheet['!ref'] ? XLSX.utils.decode_range(sheet['!ref']).s.r + 1 : 1;
  const isBlank = (cells: Array<string | number | null> | undefined) =>
    !cells || cells.every((v) => v === null || v === undefined || (typeof v === 'string' && v.trim() === ''));
  const headerIdx = matrix.findIndex((cells) => !isBlank(cells));
  if (headerIdx < 0) return { ok: false, problem: 'FILE_EMPTY' };
  const headers = (matrix[headerIdx] ?? []).map((h) => canonicalHeader(String(h ?? '')));
  // R4-02 — two columns with the same header: the later one would silently
  // overwrite the earlier one in each row object. Refuse the file instead.
  const seen = new Set<string>();
  const dup = new Set<string>();
  for (const h of headers) {
    if (!h) continue;
    if (seen.has(h)) dup.add(h);
    seen.add(h);
  }
  if (dup.size > 0) return { ok: false, problem: 'DUPLICATE_COLUMNS', columns: [...dup] };
  const missing = REQUIRED_COLUMNS.filter((c) => !headers.includes(c));
  // An old-template file still goes to the server: it answers with ONE
  // TEMPLATE_OUTDATED instead of a wall of REQUIRED. Only block when the
  // header row is not a template at all.
  const legacy = headers.includes('sku_prefix') || headers.includes('cost_price_cents');
  if (missing.length > 0 && !legacy) return { ok: false, problem: 'MISSING_COLUMNS', columns: missing };

  const rows: ImportRow[] = [];
  for (let i = headerIdx + 1; i < matrix.length; i += 1) {
    const cells = matrix[i] ?? [];
    const out: ImportRow = { row: firstRow + i };
    let any = false;
    headers.forEach((h, j) => {
      if (!h) return;
      const v = cells[j];
      if (v === null || v === undefined || (typeof v === 'string' && v.trim() === '')) return;
      out[h] = typeof v === 'number' ? v : String(v).trim();
      any = true;
    });
    if (any) rows.push(out);
  }
  if (rows.length === 0) return { ok: false, problem: 'FILE_EMPTY' };
  if (rows.length > MAX_ROWS) return { ok: false, problem: 'TOO_MANY_ROWS' };
  return { ok: true, rows };
}

/**
 * CR5-01 layer 1 — total uncompressed size DECLARED by a zip's central
 * directory, without inflating anything. Only a cheap first look: the file
 * writes these numbers itself (CR6-01), so `zipInflatedBytes` is the check.
 * `null` when the bytes are not a zip (no end-of-central-directory record): SheetJS then decides. A ZIP64 marker
 * (0xFFFFFFFF) counts as "too big" — no import template needs ZIP64.
 */
export function zipUncompressedBytes(buf: ArrayBuffer): number | null {
  const v = new DataView(buf);
  const len = buf.byteLength;
  // EOCD is 22 bytes + a comment of at most 65 535 bytes, at the very end.
  let eocd = -1;
  for (let i = len - 22; i >= Math.max(0, len - 22 - 0xffff); i -= 1) {
    if (v.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) return null;
  const entries = v.getUint16(eocd + 10, true);
  let off = v.getUint32(eocd + 16, true);
  let total = 0;
  for (let n = 0; n < entries; n += 1) {
    if (off + 46 > len || v.getUint32(off, true) !== 0x02014b50) return null;
    const size = v.getUint32(off + 24, true);
    if (size === 0xffffffff) return Number.POSITIVE_INFINITY;
    total += size;
    off += 46 + v.getUint16(off + 28, true) + v.getUint16(off + 30, true) + v.getUint16(off + 32, true);
  }
  return total;
}

const ZIP64_MARK = 0xffffffff;

/** Inflated length of one raw-deflate stream, stopping once it passes `budget`. */
async function inflatedLength(data: Uint8Array, budget: number): Promise<number> {
  const ds = new DecompressionStream('deflate-raw');
  const writer = ds.writable.getWriter();
  // The write side settles when the reader cancels or the stream errors; the
  // reader below reports either, so these promises only need not to go unhandled.
  writer.write(data as Uint8Array<ArrayBuffer>).catch(() => undefined);
  writer.close().catch(() => undefined);
  const reader = ds.readable.getReader();
  let n = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return n;
    n += value.byteLength;
    if (n > budget) {
      await reader.cancel().catch(() => undefined); // over budget: stop inflating, the size is all we need
      return Number.POSITIVE_INFINITY;
    }
  }
}

/**
 * CR6-01 — the REAL uncompressed size of a zip, counted while inflating each
 * entry and abandoned as soon as it passes `limit` (returns Infinity). The
 * sizes the central directory declares are written by whoever made the file,
 * so a bomb can claim 1 KB; only the inflated bytes are trusted here. Nothing
 * is kept — each entry streams through `DecompressionStream` and only its
 * length is counted, so the main thread never holds more than one chunk.
 *
 * `null` when the bytes are not a zip (SheetJS then decides). A zip we cannot
 * walk or inflate (bad offsets, broken deflate stream, unknown method) is
 * reported as Infinity: SheetJS would fail on it too, and refusing early is
 * cheaper than finding out in the worker.
 */
export async function zipInflatedBytes(buf: ArrayBuffer, limit: number = MAX_UNZIPPED_BYTES): Promise<number | null> {
  const declared = zipUncompressedBytes(buf);
  if (declared === null) return null;
  if (declared > limit) return Number.POSITIVE_INFINITY; // already over on its own word
  // CR7-01 — no DecompressionStream (Chrome < 103, Safari < 16.4, Firefox <
  // 113): the real count is impossible, so fall back to the declared size —
  // the worker timeout still caps a bomb — instead of refusing every XLSX.
  if (typeof DecompressionStream === 'undefined') return declared;
  const v = new DataView(buf);
  const bytes = new Uint8Array(buf);
  const len = buf.byteLength;
  let eocd = -1;
  for (let i = len - 22; i >= Math.max(0, len - 22 - 0xffff); i -= 1) {
    if (v.getUint32(i, true) === 0x06054b50) {
      eocd = i;
      break;
    }
  }
  const entries = v.getUint16(eocd + 10, true);
  let off = v.getUint32(eocd + 16, true);
  let total = 0;
  try {
    for (let n = 0; n < entries; n += 1) {
      const method = v.getUint16(off + 10, true);
      const cdCompressed = v.getUint32(off + 20, true);
      const local = v.getUint32(off + 42, true);
      if (cdCompressed === ZIP64_MARK || local === ZIP64_MARK) return Number.POSITIVE_INFINITY;
      if (local + 30 > len || v.getUint32(local, true) !== 0x04034b50) return Number.POSITIVE_INFINITY;
      // Same rule a zip reader follows: the local header's compressed size,
      // or the central directory's when bit 3 (data descriptor) is set.
      const flags = v.getUint16(local + 6, true);
      const compressed = flags & 0x08 ? cdCompressed : v.getUint32(local + 18, true);
      const start = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true);
      const end = Math.min(len, start + compressed);
      if (start > len) return Number.POSITIVE_INFINITY;
      if (method === 0) total += end - start;
      else if (method === 8) total += await inflatedLength(bytes.subarray(start, end), limit - total);
      else return Number.POSITIVE_INFINITY;
      if (total > limit) return Number.POSITIVE_INFINITY;
      off += 46 + v.getUint16(off + 28, true) + v.getUint16(off + 30, true) + v.getUint16(off + 32, true);
    }
  } catch {
    // Broken deflate data or an out-of-range offset: unreadable — see above.
    return Number.POSITIVE_INFINITY;
  }
  return total;
}

/** Runs `parseWorkbook` somewhere; the default is a Web Worker (see below). */
export type WorkbookParser = (data: ArrayBuffer, kind: 'csv' | 'xlsx') => Promise<ReadResult>;

/** The subset of `Worker` used here, so a test can hand in a fake. */
export interface ParseWorker {
  onmessage: ((ev: MessageEvent<ReadResult>) => void) | null;
  onerror: ((ev: ErrorEvent) => void) | null;
  /** CR6-02 — the worker's answer could not be deserialised. */
  onmessageerror: ((ev: MessageEvent) => void) | null;
  postMessage(msg: { data: ArrayBuffer; kind: 'csv' | 'xlsx' }, transfer: Transferable[]): void;
  terminate(): void;
}

/**
 * CR5-01 layer 2 — parse off the main thread and give up after `timeoutMs`:
 * a file the size check let through can still be slow to unzip/parse, and a
 * stuck worker is killed instead of freezing the vendor's tab.
 */
export function parseInWorker(
  createWorker: () => ParseWorker,
  timeoutMs: number = PARSE_TIMEOUT_MS,
): WorkbookParser {
  return (data, kind) =>
    new Promise<ReadResult>((resolve) => {
      const worker = createWorker();
      let done = false;
      const finish = (res: ReadResult) => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        worker.terminate();
        resolve(res);
      };
      // CR6-02 — running out of time means the file is too heavy to parse here,
      // not that it is broken: say so, so the vendor splits it instead of re-saving it.
      const timer = setTimeout(() => finish({ ok: false, problem: 'PARSE_TIMEOUT' }), timeoutMs);
      worker.onmessage = (ev) => finish(ev.data);
      worker.onerror = (ev) => {
        // A crash inside the worker is a file we could not read; say so.
        ev.preventDefault();
        finish({ ok: false, problem: 'READ_FAILED' });
      };
      // CR6-02 — an answer that cannot be deserialised would otherwise sit
      // until the timeout and be reported as "too heavy".
      worker.onmessageerror = () => finish({ ok: false, problem: 'READ_FAILED' });
      worker.postMessage({ data, kind }, [data]);
    });
}

// Resolved per call (not at import) so the Worker global is looked up when a
// file is actually picked.
const defaultParser: WorkbookParser = (data, kind) =>
  typeof Worker === 'undefined'
    ? Promise.resolve(parseWorkbook(data, kind)) // no Worker (SSR / plain jsdom): parse inline
    : parseInWorker(
        () => new Worker(new URL('./importSheet.worker.ts', import.meta.url), { type: 'module' }) as unknown as ParseWorker,
      )(data, kind);

/** Check + read a picked file. Nothing is sent when this fails. */
export async function readImportFile(file: File, parse: WorkbookParser = defaultParser): Promise<ReadResult> {
  const kind = extensionOf(file.name);
  if (!kind) return { ok: false, problem: 'FILE_TYPE' };
  if (file.size === 0) return { ok: false, problem: 'FILE_EMPTY' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, problem: 'FILE_TOO_LARGE' };
  try {
    const data = await file.arrayBuffer();
    if (kind === 'xlsx') {
      // CR6-01 — the inflated size, not what the central directory claims.
      const unzipped = await zipInflatedBytes(data, MAX_UNZIPPED_BYTES);
      if (unzipped !== null && unzipped > MAX_UNZIPPED_BYTES) return { ok: false, problem: 'READ_FAILED' };
    }
    return await parse(data, kind);
  } catch {
    return { ok: false, problem: 'READ_FAILED' };
  }
}

export interface PrintwaySkuRow {
  blueprint_id: string;
  blueprint_name: string;
  sku: string;
  size: string;
  color: string;
}

/**
 * The template workbook. The example row uses a REAL Printway SKU from the
 * catalogue when there is one (never a made-up price); the Guide sheet text
 * comes translated from the caller.
 */
export function buildTemplate(opts: {
  kind: 'csv' | 'xlsx';
  skus: PrintwaySkuRow[];
  guide: Array<[string, string]>;
}): Blob {
  const example = opts.skus.slice(0, 2);
  const rows: Array<Record<string, string>> = example.map((s, i) => ({
    product_code: 'MYTEE01',
    product_name: i === 0 ? s.blueprint_name : '',
    printway_sku: s.sku,
    size: s.size,
    color: s.color,
  }));
  const importSheet = XLSX.utils.json_to_sheet(rows, { header: [...TEMPLATE_COLUMNS] });
  if (opts.kind === 'csv') {
    const csv = XLSX.utils.sheet_to_csv(importSheet);
    return new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, importSheet, IMPORT_SHEET);
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(
      opts.skus.map((s) => ({ blueprint: s.blueprint_name, printway_sku: s.sku, size: s.size, color: s.color })),
      { header: ['blueprint', 'printway_sku', 'size', 'color'] },
    ),
    SKU_SHEET,
  );
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(opts.guide), GUIDE_SHEET);
  const out = XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
  return new Blob([out], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}
